import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import CandidateLinksTable, { type CandidateLinkRow } from '../../components/employer/CandidateLinksTable';
import CreditBar from '../../components/employer/CreditBar';
import EmployerPageShell from '../../components/employer/EmployerPageShell';
import ReportsInbox, { type ReportInboxRow, type ReportInboxStatus } from '../../components/employer/ReportsInbox';
import SimulationsTable, { type SimulationRow, type SimulationStatus } from '../../components/employer/SimulationsTable';
import { FEATURES } from '../../config/featureFlags';
import api, { fetchCommerceBuildRequests, type CommerceBuildRequest, type CommerceBuildRequestStatus } from '../../services/api';

type DashboardTab = 'simulations' | 'reports' | 'links' | 'buildRequests';

type WalletResponse = {
  credits_available: number;
  reserved_balance?: number;
};

type AvailableSimulationsResponse = {
  simulations: Array<{
    simulationCode: string;
    title: string;
    role: string;
    defaultVersionId: string | null;
    defaultVersionLabel: string | null;
    disabledReason: string | null;
    isGloballyEnabled: boolean;
    hasAccess: boolean;
    reportsReadyCount: number;
  }>;
};

type EmployerResultItem = {
  completed_at: string;
  candidate: {
    full_name: string;
  };
  performance: {
    percent: number;
  };
  link: {
    job_title: string | null;
  };
  report: {
    report_url: string | null;
    reviewed_at: string | null;
  };
};

type EmployerResultsResponse = {
  items: EmployerResultItem[];
};

type EmployerLinksResponse = {
  items: Array<{
    created_at: string;
    expires_at: string | null;
    job_title: string | null;
    public_url: string;
    simulation: {
      name: string;
      version: string;
    };
    status: 'ACTIVE' | 'EXPIRED';
  }>;
};

const formatDate = (isoValue: string | null | undefined): string => {
  if (!isoValue) return '—';
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const getSimulationStatus = (simulation: AvailableSimulationsResponse['simulations'][number]): SimulationStatus => {
  if (!simulation.isGloballyEnabled || !simulation.hasAccess) return 'CLOSED';
  if (!simulation.defaultVersionId) return 'DRAFT';
  return 'ACTIVE';
};

const getBuildRequestStatusLabel = (status: CommerceBuildRequestStatus) => {
  switch (status) {
    case 'PAID':
    case 'SCOPING':
      return 'Allocated for build';
    case 'IN_BUILD':
      return 'Final checks';
    case 'DELIVERED':
      return 'Deployed to dashboard';
    case 'INVOICE_REQUESTED':
      return 'Invoice requested';
    case 'AWAITING_BILLING':
      return 'Awaiting invoice details';
    case 'PENDING':
      return 'Pending payment';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return status;
  }
};

const shouldLogDashboardPerf = (): boolean => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('perf_debug') === '1' || import.meta.env.DEV;
};

const copyText = async (text: string) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const fallback = document.createElement('textarea');
  fallback.value = text;
  fallback.setAttribute('readonly', 'true');
  fallback.style.position = 'absolute';
  fallback.style.left = '-9999px';
  document.body.appendChild(fallback);
  fallback.select();
  document.execCommand('copy');
  document.body.removeChild(fallback);
};

const SimulationDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DashboardTab>('simulations');
  const [wallet, setWallet] = useState<WalletResponse | null>(null);
  const [simulations, setSimulations] = useState<AvailableSimulationsResponse['simulations']>([]);
  const [reports, setReports] = useState<EmployerResultItem[]>([]);
  const [links, setLinks] = useState<EmployerLinksResponse['items']>([]);
  const [buildRequests, setBuildRequests] = useState<CommerceBuildRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      const perfEnabled = shouldLogDashboardPerf();
      const loadStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
      if (perfEnabled) {
        console.info('[EMPLOYER_DASHBOARD_LOAD_START]', {
          startedAtMs: loadStartedAt,
        });
      }

      const walletRequest = api.get<WalletResponse>('/sim/wallet');
      const availableRequest = api.get<AvailableSimulationsResponse>('/sim/available');
      const reportsRequest = api.get<EmployerResultsResponse>('/sim/employer/results', { params: { limit: 25 } });
      const linksRequest = api.get<EmployerLinksResponse>('/sim/links', { params: { limit: 100 } });
      const buildRequestsRequest = fetchCommerceBuildRequests(api);

      walletRequest
        .then((result) => {
          if (!mounted) return;
          setWallet(result.data);
          if (perfEnabled) {
            console.info('[EMPLOYER_DASHBOARD_WALLET_RESOLVED]', {
              durationMs:
                (typeof performance !== 'undefined' ? performance.now() : Date.now()) - loadStartedAt,
              creditsAvailable: result.data?.credits_available ?? 0,
            });
          }
        })
        .catch(() => {
          if (!mounted) return;
          setWallet({ credits_available: 0, reserved_balance: 0 });
          if (perfEnabled) {
            console.info('[EMPLOYER_DASHBOARD_WALLET_FAILED]', {
              durationMs:
                (typeof performance !== 'undefined' ? performance.now() : Date.now()) - loadStartedAt,
            });
          }
        });

      availableRequest
        .then((result) => {
          if (!mounted) return;
          setSimulations(Array.isArray(result.data?.simulations) ? result.data.simulations : []);
          if (perfEnabled) {
            console.info('[EMPLOYER_DASHBOARD_AVAILABLE_RESOLVED]', {
              durationMs:
                (typeof performance !== 'undefined' ? performance.now() : Date.now()) - loadStartedAt,
              count: Array.isArray(result.data?.simulations) ? result.data.simulations.length : 0,
            });
          }
        })
        .catch(() => {
          if (!mounted) return;
          setSimulations([]);
          if (perfEnabled) {
            console.info('[EMPLOYER_DASHBOARD_AVAILABLE_FAILED]', {
              durationMs:
                (typeof performance !== 'undefined' ? performance.now() : Date.now()) - loadStartedAt,
            });
          }
        });

      reportsRequest
        .then((result) => {
          if (!mounted) return;
          const nextReports = Array.isArray(result.data?.items) ? result.data.items : [];
          setReports(nextReports);
          if (perfEnabled) {
            console.info('[EMPLOYER_DASHBOARD_RESULTS_RESOLVED]', {
              durationMs:
                (typeof performance !== 'undefined' ? performance.now() : Date.now()) - loadStartedAt,
              count: nextReports.length,
            });
          }
        })
        .catch(() => {
          if (!mounted) return;
          setReports([]);
          if (perfEnabled) {
            console.info('[EMPLOYER_DASHBOARD_RESULTS_FAILED]', {
              durationMs:
                (typeof performance !== 'undefined' ? performance.now() : Date.now()) - loadStartedAt,
            });
          }
        });

      linksRequest
        .then((result) => {
          if (!mounted) return;
          setLinks(Array.isArray(result.data?.items) ? result.data.items : []);
          if (perfEnabled) {
            console.info('[EMPLOYER_DASHBOARD_LINKS_RESOLVED]', {
              durationMs:
                (typeof performance !== 'undefined' ? performance.now() : Date.now()) - loadStartedAt,
              count: Array.isArray(result.data?.items) ? result.data.items.length : 0,
            });
          }
        })
        .catch(() => {
          if (!mounted) return;
          setLinks([]);
          if (perfEnabled) {
            console.info('[EMPLOYER_DASHBOARD_LINKS_FAILED]', {
              durationMs:
                (typeof performance !== 'undefined' ? performance.now() : Date.now()) - loadStartedAt,
            });
          }
        });

      buildRequestsRequest
        .then((items) => {
          if (!mounted) return;
          setBuildRequests(items);
        })
        .catch(() => {
          if (!mounted) return;
          setBuildRequests([]);
        });

      await Promise.allSettled([walletRequest, availableRequest, reportsRequest, linksRequest, buildRequestsRequest]);

      if (!mounted) return;
      setLoading(false);
      if (perfEnabled) {
        console.info('[EMPLOYER_DASHBOARD_LOAD_COMPLETE]', {
          durationMs:
            (typeof performance !== 'undefined' ? performance.now() : Date.now()) - loadStartedAt,
        });
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, []);

  const simulationRows = useMemo<SimulationRow[]>(() => {
    return simulations.map((simulation) => {
      const status = getSimulationStatus(simulation);

      return {
        simulationCode: simulation.simulationCode,
        role: simulation.role,
        variants: simulation.defaultVersionId ? 1 : 0,
        reportsReady: simulation.reportsReadyCount,
        status,
        actionLabel: status === 'DRAFT' ? 'Edit' : 'View Reports',
        onAction: () => {
          if (status === 'DRAFT') {
            navigate('/employer/admin?tab=simulations');
            return;
          }
          navigate(`/employer/sim/results?simulationCode=${encodeURIComponent(simulation.simulationCode)}`);
        },
      };
    });
  }, [navigate, simulations]);

  const reportRows = useMemo<ReportInboxRow[]>(() => {
    return reports.map((report, index) => {
      const reportUrl = report.report?.report_url || '';
      const status: ReportInboxStatus = !reportUrl
        ? 'PROCESSING'
        : Boolean(report.report?.reviewed_at)
          ? 'REVIEWED'
          : 'NEW';

      return {
        id: `${report.candidate.full_name}-${report.completed_at}-${index}`,
        candidate: report.candidate.full_name,
        simulation: report.link.job_title || 'Role Simulation',
        overallScore: `${report.performance.percent}%`,
        submitted: formatDate(report.completed_at),
        status,
        onOpen: () => {
          if (!reportUrl) return;
          navigate(reportUrl);
        },
      };
    });
  }, [navigate, reports]);

  const linkRows = useMemo<CandidateLinkRow[]>(() => {
    return links.map((link) => {
      const simulationLabel = [link.simulation?.name, link.simulation?.version].filter(Boolean).join(' • ') || 'Simulation Link';
      return {
        id: link.public_url,
        simulation: simulationLabel,
        jobReferenceTitle: link.job_title || '—',
        status: link.status,
        expires: formatDate(link.expires_at),
        onCopy: async () => {
          try {
            await copyText(link.public_url);
            toast.success('Candidate link copied.');
          } catch (_error) {
            toast.error('Unable to copy candidate link.');
          }
        },
      };
    });
  }, [links]);

  useEffect(() => {
    if (!shouldLogDashboardPerf() || !wallet) return;
    console.info('[EMPLOYER_DASHBOARD_CREDITS_RENDERED]', {
      creditsAvailable: wallet.credits_available ?? 0,
      reservedBalance: wallet.reserved_balance ?? 0,
    });
  }, [wallet]);

  const tabs = [
    { key: 'simulations' as const, label: 'Simulations', count: simulations.length, countClass: 'bg-[#f0fdfa] text-[#0d9488]' },
    { key: 'buildRequests' as const, label: 'Build Requests', count: buildRequests.length, countClass: 'bg-[#ecfeff] text-[#0891b2]' },
    { key: 'reports' as const, label: 'Reports' },
    { key: 'links' as const, label: 'Candidate Links' },
  ];

  return (
    <EmployerPageShell
      activeSection="dashboard"
      navChildren={
        <>
          {/* HIDDEN FOR SIMULATION LAUNCH — DO NOT DELETE — will be re-enabled for full platform launch */}
          {FEATURES.JOB_MANAGEMENT && (
            <button
              type="button"
              onClick={() => navigate('/employer/jobs')}
              className="rounded-[10px] border border-[#e2e5eb] bg-white px-3 py-2 text-[12px] font-semibold text-[#475569]"
            >
              Manage Jobs
            </button>
          )}
          {/* HIDDEN FOR SIMULATION LAUNCH — DO NOT DELETE — will be re-enabled for full platform launch */}
          {FEATURES.VIDEO_INTERVIEWS && (
            <button
              type="button"
              onClick={() => navigate('/employer/interviews')}
              className="rounded-[10px] border border-[#e2e5eb] bg-white px-3 py-2 text-[12px] font-semibold text-[#475569]"
            >
              Review Interviews
            </button>
          )}
          {/* HIDDEN FOR SIMULATION LAUNCH — DO NOT DELETE — will be re-enabled for full platform launch */}
          {FEATURES.CANDIDATE_SCREENING && (
            <button
              type="button"
              onClick={() => navigate('/employer/reports')}
              className="rounded-[10px] border border-[#e2e5eb] bg-white px-3 py-2 text-[12px] font-semibold text-[#475569]"
            >
              Candidate Screening
            </button>
          )}
        </>
      }
    >
      <section className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-[24px] font-bold text-[#0f172a]">Role Simulations</h1>
          <p className="mt-1 text-[13px] text-[#64748b]">Create, manage, and review candidate assessments</p>
        </div>
      </section>

      <CreditBar
        creditsAvailable={wallet?.credits_available ?? 0}
        creditsReserved={wallet?.reserved_balance ?? 0}
        onTopUp={() => navigate('/employer/billing')}
      />

      <section>
        <div className="rounded-t-[14px] border border-[#e2e5eb] border-b-2 bg-white">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`-mb-[2px] flex shrink-0 items-center gap-2 border-b-2 px-5 py-3.5 text-[13px] font-semibold transition-colors ${
                    isActive ? 'border-[#0d9488] text-[#0d9488]' : 'border-transparent text-[#94a3b8] hover:text-[#475569]'
                  }`}
                >
                  <span>{tab.label}</span>
                  {'count' in tab ? (
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${tab.countClass}`}>
                      {tab.count}
                    </span>
                  ) : null}
                  {'countLabel' in tab ? (
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${tab.countClass}`}>
                      {tab.countLabel}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="rounded-b-[14px] border border-t-0 border-[#e2e5eb] bg-white px-6 py-10">
            <p className="text-[13px] text-[#64748b]">Loading simulation data...</p>
          </div>
        ) : null}

        {!loading && activeTab === 'simulations' ? <SimulationsTable rows={simulationRows} /> : null}
        {!loading && activeTab === 'buildRequests' ? (
          <div className="rounded-b-[14px] border border-t-0 border-[#e2e5eb] bg-white">
            {buildRequests.length === 0 ? (
              <div className="px-6 py-10 text-[13px] text-[#64748b]">No build requests yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#e2e8f0] text-left text-[13px]">
                  <thead className="bg-[#f8fafc] text-[11px] font-semibold uppercase tracking-[0.12em] text-[#64748b]">
                    <tr>
                      <th className="px-5 py-3">Build request</th>
                      <th className="px-5 py-3">Payment</th>
                      <th className="px-5 py-3">Current status</th>
                      <th className="px-5 py-3">Simulation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {buildRequests.map((request) => (
                      <tr key={request.id}>
                        <td className="px-5 py-3">
                          <div className="font-semibold text-[#0f172a]">{request.roleName}</div>
                          <div className="text-[12px] text-[#64748b]">{request.offering.name}</div>
                        </td>
                        <td className="px-5 py-3 text-[#334155]">{request.paymentOrder?.paymentStatus || 'Not linked'}</td>
                        <td className="px-5 py-3">
                          <span className="rounded-full bg-[#f0fdfa] px-3 py-1 text-[12px] font-semibold text-[#0f766e]">
                            {getBuildRequestStatusLabel(request.status)}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-[#334155]">
                          {request.status === 'DELIVERED' ? `${request.roleName} deployed` : 'Not deployed yet'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}
        {!loading && activeTab === 'reports' ? (
          <ReportsInbox rows={reportRows} onViewAll={() => navigate('/employer/sim/results')} />
        ) : null}
        {!loading && activeTab === 'links' ? (
          <CandidateLinksTable rows={linkRows} onGenerate={() => navigate('/sim/create-link')} />
        ) : null}
      </section>
    </EmployerPageShell>
  );
};

export default SimulationDashboard;
